import React, { useState, useEffect } from "react";
import axios from "axios";
import ConfigNet from "./ConfigNet";
import WiredSettings from "./WiredSettings";
import IpConfiguration from "./IpConfiguration";
import WirelessSettings from "./WirelessSettings";
import allApi from "../../../../api/allApi";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "../../localization/LocalizationContext";
import { useLocation } from "react-router-dom";
import { asSwitchList } from "../../common/configValidation";

function ConfigureNetwork({ onMacAclApiCall } = {}) {
  const location = useLocation();
  const { localizationStrings } = useLocalization();
  const [currentStep, setCurrentStep] = useState(1);
  const [configNetData, setConfigNetData] = useState({});
  const [wiredSettingsData, setWiredSettingsData] = useState(null);
  const [wirelessSettingsData, setWirelessSettingsData] = useState({});
  const [ipConfigurationData, setIpConfigurationData] = useState({});
  const [isStep1Valid, setIsStep1Valid] = useState(false);
  const [onDataChange, setOnDataChange] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();
  const [macAclPayload, setMacAclPayload] = useState(null);

  useEffect(() => {}, [wiredSettingsData]);

  useEffect(() => {
    if (macAclPayload && typeof onMacAclApiCall === "function") {
      onMacAclApiCall(macAclPayload);
    }
  }, [macAclPayload, onMacAclApiCall]);

  useEffect(() => {
    const fetchSwitches = async () => {
      try {
        const api = new allApi();
        const response = await api.getRequest("api/vlan/get/allSwitchBasicInfo");
        setBasicSwitchInfo(asSwitchList(response));
      } catch (error) {
        console.error("Failed to fetch switch info:", error);
        setBasicSwitchInfo([]);
      }
    };
    fetchSwitches();
  }, []);

  const [formData, setFormData] = useState({
    vlanId: "",
    serialNumber: "",
    taggedPorts: "",
    untaggedPorts: "",
  });
  const [schedule, setSchedule] = useState({
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
    Sunday: [],
  });
  const [loading, setLoading] = useState(true);
  const [ssidList, setSsidList] = useState([]);
  const [basicSwitchInfo, setBasicSwitchInfo] = useState(null);
  const [refreshing, setRefreshing] = useState(true);
  const [assignedPorts, setAssignedPorts] = useState({});
  const [voiceOptimization, setVoiceOptimization] = useState(false);
  const [videoOptimization, setVideoOptimization] = useState(false);
  const [vlanId, setVlanId] = useState("");
  const [vlanName, setVlanName] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorPopup, setErrorPopup] = useState(null);
  const [vlanList, setVlanList] = useState([]);
  const editVlanId = location.state?.editVlanId;
  const isEditMode = !!editVlanId;
  const vlanDetails = location.state?.vlanDetails;

  const [vlanData, setVlanData] = useState({
    igmpSnooping: "0",
    name: "",
    overrideTrafficPriority: "0",
    qosConfig: "dataNetwork",
    trafficClass: "0",
    vlanId: "",
    vlanNwDesc: "",
    vlanNwName: "",
    networkId: "",
    vlanType: "6",
    voipOptimization: "0",
    ipMode: "none",
  });

  const handleWiredSettingsDataChange = (updatedPayloadList) => {
    setWiredSettingsData(updatedPayloadList);
  };

  useEffect(() => {
    if (editVlanId && vlanData.name && vlanData.vlanId && vlanData.vlanNwName) {
      setIsStep1Valid(true);
    }
  }, [vlanData, editVlanId]);

  useEffect(() => {
    if (vlanDetails) {
      setVlanData((prev) => ({
        ...prev,
        ...vlanDetails,
      }));

      setConfigNetData({
        vlanId: vlanDetails.vlanId || "",
        vlanName: vlanDetails.name || "",
        description: vlanDetails.vlanNwDesc || "",
        networkName: "",
        networkType: vlanDetails.qosConfig || "",
        networkId: vlanDetails.networkId || "",
      });

      if (vlanDetails.networkId) {
        fetchNetworkName(vlanDetails.networkId);
      }
    }
  }, [vlanDetails]);

  const handleStepClick = async (step) => {
    if (currentStep === 1 && step > 1 && !isStep1Valid) {
      return;
    }

    if (step > currentStep) {
      const isValid = await validateStep(currentStep);
      if (!isValid) return;
    }

    setCurrentStep(step);
  };

  const createScheduleSetting = (schedule) => {
    return {
      schDays: "1111111", // Customize as per selected days
      schOnTime0: schedule.Monday[0]?.startTime || "0000",
      schOffTime0: schedule.Monday[0]?.endTime || "0000",
      schOnTime1: schedule.Tuesday[0]?.startTime || "0000",
      schOffTime1: schedule.Tuesday[0]?.endTime || "0000",
      schOnTime2: schedule.Wednesday[0]?.startTime || "0000",
      schOffTime2: schedule.Wednesday[0]?.endTime || "0000",
      schOnTime3: schedule.Thursday[0]?.startTime || "0000",
      schOffTime3: schedule.Thursday[0]?.endTime || "0000",
      schOnTime4: schedule.Friday[0]?.startTime || "0000",
      schOffTime4: schedule.Friday[0]?.endTime || "0000",
      schOnTime5: schedule.Saturday[0]?.startTime || "0000",
      schOffTime5: schedule.Saturday[0]?.endTime || "0000",
      schOnTime6: schedule.Sunday[0]?.startTime || "0000",
      schOffTime6: schedule.Sunday[0]?.endTime || "0000",
    };
  };

  const validateStep = async (step) => {
    switch (step) {
      case 1:
        return isStep1Valid;
      case 2:
        return true;
      case 3: {
        const ssid = String(wirelessSettingsData?.ssid || "").trim();
        if (!ssid) return true;
        return ssid.length <= 32;
      }
      case 4: {
        const mode = vlanData.ipMode || "none";
        return ["DHCP", "static", "none"].includes(mode);
      }
      default:
        return false;
    }
  };

  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (!isValid) return;
    if (currentStep === 4) {
      await handleSubmit();
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleMacAclDataChange = (data) => {
    setMacAclPayload(data);
  };

  const handleSaveOptimization = async (type, value) => {
    try {
      const payload = {
        vlanId: vlanId,
        vlanName: vlanName,
        voiceOptimization: voiceOptimization,
        videoOptimization: videoOptimization,
      };

      const api = new allApi();
      const response = await api.postRequest(
        "api/vlan/voiceVideoOptimization",
        "",
        payload
      );

      if (response) {
      }
    } catch (error) {
      console.error("Error during API call:", error);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const api = new allApi();
      const networkId = Number(vlanData.networkId || configNetData.networkId || 0);
      const vlanId = String(vlanData.vlanId);

      let accessPortVlanInfo = [];

      try {
        const accessPortResponse = await api.getRequest(
          `api/vlan/v2/get/accessPort?networkId=${networkId}`
        );

        const devices = accessPortResponse?.response?.devices || [];

        accessPortVlanInfo = devices.length
          ? devices.map((device) => ({
              serialNumber: device.serialNo || device.serialNumber,
              vlanMembers: {
                apPortMembers: [],
                employeeMembers: [],
                guestMembers: [],
                lagMembers: {
                  portGrp: [],
                  tagged: [],
                  untagged: [],
                },
                lanPortMembers: [],
                memberWirelessNetwork: [],
                portMembers: [
                  {
                    taggedPorts: device.tagged_ports || [],
                    untaggedPorts: device.untagged_ports || [],
                  },
                ],
              },
            }))
          : (wiredSettingsData || []).map((device) => ({
              serialNumber: device.SerialNumber || device.serialNumber,
              vlanMembers: device.vlanMembers || {
                apPortMembers: [],
                employeeMembers: [],
                guestMembers: [],
                lagMembers: {
                  portGrp: [],
                  tagged: [],
                  untagged: [],
                },
                lanPortMembers: [],
                memberWirelessNetwork: [],
                portMembers: [
                  {
                    taggedPorts: [],
                    untaggedPorts: [],
                  },
                ],
              },
            }));
      } catch (accessPortError) {
        console.warn(
          "Unable to fetch access port details from v2 endpoint; falling back to existing wired payload.",
          accessPortError
        );

        accessPortVlanInfo = (wiredSettingsData || []).map((device) => ({
          serialNumber: device.SerialNumber || device.serialNumber,
          vlanMembers: device.vlanMembers || {
            apPortMembers: [],
            employeeMembers: [],
            guestMembers: [],
            lagMembers: {
              portGrp: [],
              tagged: [],
              untagged: [],
            },
            lanPortMembers: [],
            memberWirelessNetwork: [],
            portMembers: [
              {
                taggedPorts: [],
                untaggedPorts: [],
              },
            ],
          },
        }));
      }

      const requestPayload = {
        networkId,
        vlan: {
          vlanId: Number(vlanId),
          igmpSnooping: "1",
          name: vlanData.name,
          overrideTrafficPriority: "0",
          qosConfig: vlanData.qosConfig || "dataNetwork",
          vlanNwName: vlanData.vlanNwName,
          trafficClass: "0",
          vlanNwDesc: vlanData.vlanNwDesc,
          vlanType: "6",
          voipOptimization: "0",
        },
        accessPortFlag: true,
        accessPort: {
          vlanId,
          vlanInfo: accessPortVlanInfo,
        },
        voiceVideoOptimizationFlag: false,
        voiceVideoOptimization: {
          voiceOptimization,
          videoOptimization,
          vlanId: Number(vlanId),
          vlanName: vlanData.name,
        },
        macAclFlag: false,
        macAclDTO: {
          vlanId: Number(vlanId),
          macAclList: {
            mode: "0",
            action: "0",
            macAclRule: {
              manual: [],
              custom: [],
            },
          },
        },
        ssidFlag: Boolean(String(wirelessSettingsData?.ssid || formData?.ssid || "").trim()),
        ssidDTO: {
          ssid: wirelessSettingsData?.ssid || formData?.ssid || "",
          broadcastStatus:
            wirelessSettingsData?.broadcastStatus ?? formData?.broadcastStatus ?? 0,
          band: Array.isArray(wirelessSettingsData?.band)
            ? wirelessSettingsData.band.join(", ")
            : wirelessSettingsData?.band || formData?.band || "",
          vlanId: Number(vlanId),
          securityAuthentication:
            wirelessSettingsData?.securityAuthentication ||
            formData?.securityAuthentication ||
            "0",
          bandSteeringSt:
            wirelessSettingsData?.bandSteeringSt || formData?.bandSteeringSt || "0",
          fastRoamingSt:
            wirelessSettingsData?.fastRoamingSt || formData?.fastRoamingSt || "0",
          clientIsolation:
            wirelessSettingsData?.clientIsolation || formData?.clientIsolation || "0",
          wn_enable: 1,
          allowLocalUIAccess:
            wirelessSettingsData?.allowLocalUIAccess ||
            formData?.allowLocalUIAccess ||
            "0",
          roamingOptimization: 0,
          serialNumber: formData?.serialNumber || "",
          ssidPosition: 1,
          scheduleEnabled:
            wirelessSettingsData?.scheduleEnabled || formData?.scheduleEnabled || "0",
          schedule:
            (wirelessSettingsData?.scheduleEnabled || formData?.scheduleEnabled) === "1"
              ? {
                  scheduleName: formData?.selectedSchedule || "custom_schedule",
                  allDay: formData?.allDay || "0",
                  scheduleSetting: createScheduleSetting(schedule),
                }
              : {},
          password: wirelessSettingsData?.password || formData?.password || "",
          kvrStatus: wirelessSettingsData?.kvrStatus || formData?.kvrStatus || "0",
          ieee80211w: 0,
          mac_acl_status: 0,
          mac_acl_list: {},
        },
      };

      const response = await axios.post(
        `${window.location.origin}/api/vlan/v2/add/vlan`,
        requestPayload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
          },
        }
      );

      const responseData = response?.data;

      if (
        response?.status >= 400 ||
        responseData?.status === 400 ||
        responseData?.status === "400 BAD_REQUEST" ||
        responseData?.message ===
          "Request VLAN could not be added because of Vland Id already present"
      ) {
        setErrorPopup({
          title: "VLAN Creation Error",
          details: [
            `Status: ${responseData?.status || response?.status}`,
            `Message: ${responseData?.message || "Bad Request"}`,
          ],
          rawResponse: responseData,
        });
        return;
      }

      if (responseData?.success === false) {
        throw new Error(
          `VLAN creation failed: ${
            responseData?.errorMessage || responseData?.message || "Unknown error"
          }`
        );
      }

      setSuccessMessage(
        responseData?.message || "VLAN configuration Applied successfully"
      );
      setShowSuccessModal(true);
    } catch (error) {
      console.error("VLAN v2 submission failed:", error);
      setErrorPopup({
        title: "VLAN Creation Error",
        details: [
          error?.message || "Unexpected error while creating VLAN",
        ],
        rawResponse: error,
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchNetworkName = async (networkId) => {
    try {
      const api = new allApi();

      const response = await api.getRequest(
        `api/network/allNetwork?networkId=${networkId}`
      );

      console.log("Network API Response:", response);

      // 🔥 FIX HERE
      const networkData = response?.response;

      if (networkData?.networkName) {
        setVlanData((prev) => ({
          ...prev,
          vlanNwName: networkData.networkName,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch network name:", error);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ConfigNet
            data={configNetData}
            setData={setConfigNetData}
            setIsValid={setIsStep1Valid}
            vlanData={vlanData}
            setVlanData={setVlanData}
          />
        );
      case 2:
        return (
          <WiredSettings
            // data={wiredSettingsData}
            setData={setOnDataChange}
            serialNumber={formData.serialNumber}
            formData={formData}
            setFormData={setFormData}
            onDataChange={setWiredSettingsData}
            portDetails={Array.isArray(basicSwitchInfo) ? basicSwitchInfo : []}
            voiceOptimization={voiceOptimization}
            setVoiceOptimization={setVoiceOptimization}
            videoOptimization={videoOptimization}
            setVideoOptimization={setVideoOptimization}
            handleSaveOptimization={handleSaveOptimization}
            vlanData={vlanData}
            setVlanData={setVlanData}
          />
        );
      case 3:
        return (
          <WirelessSettings
            data={wirelessSettingsData}
            setData={setWirelessSettingsData}
            ssidList={ssidList}
            networkId={vlanData.networkId || configNetData.networkId}
            vlanData={vlanData}
            setVlanData={setVlanData}
          />
        );
      case 4:
        return (
          <IpConfiguration
            data={ipConfigurationData}
            setData={setIpConfigurationData}
            vlanData={vlanData}
            setVlanData={setVlanData}
          />
        );
      default:
        return null;
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleCancel = () => {
    // Handle cancel action
    navigate("/wired-config");
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate("/wired-config");
  };

  const closeErrorPopup = () => {
    setErrorPopup(null);
  };

  // const handleSaveVlan = async () => {
  //   const vlanPayload = {
  //     vlan: { ...vlanData },
  //   };

  //   try {
  //     const api = new allApi();
  //     let vlanResponse;

  //     if (isEditMode) {
  //       vlanResponse = await api.putRequest("api/vlan/edit/vlan", vlanPayload);
  //     } else {
  //       vlanResponse = await api.postRequest(
  //         "api/vlan/add/vlan",
  //         "",
  //         vlanPayload
  //       );
  //     }

  //     alert(
  //       isEditMode ? "VLAN updated successfully" : "VLAN created successfully"
  //     );
  //   } catch (err) {
  //     console.error("VLAN save failed", err);
  //   }
  // };

  return (
    <div className="configure-network nms-dash config-page">
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="inc-card">
              <div className="inc-card-title">
                <h3>{localizationStrings?.configureNetwork}</h3>
              </div>
              <div className="inc-card-body">
                <div className="inc-report">
                  <div className="report-tab">
                    {[1, 2, 3, 4].map((step) => (
                      <div
                        className={`step ${
                          currentStep === step
                            ? "selected"
                            : currentStep > step
                            ? "completed"
                            : ""
                        }`}
                        key={step}
                        onClick={() => handleStepClick(step)}
                      >
                        <div
                          className="s-text"
                          style={{ cursor: "pointer", color: "purple" }}
                        >
                          <span>
                            {localizationStrings?.step} {step}
                          </span>
                          <strong>
                            {step === 1
                              ? localizationStrings?.configureNetwork
                              : step === 2
                              ? localizationStrings?.wiredSettings
                              : step === 3
                              ? localizationStrings?.wirelessSettings
                              : localizationStrings?.ipConfiguration}
                          </strong>
                        </div>
                        <div className="s-dot">
                          <i
                            className={`fa ${
                              currentStep >= step ? "fa-check" : "fa-circle"
                            }`}
                            aria-hidden="true"
                          ></i>
                        </div>
                        {step < 4 && (
                          <i className="line">
                            <span></span>
                          </i>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="report-section">
                    <div className="report-container">{renderStep()}</div>
                    <div className="report-button">
                      {currentStep > 1 && (
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={handleBack}
                        >
                          {localizationStrings?.back}
                        </button>
                      )}
                      {currentStep === 1 && (
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={handleCancel}
                        >
                          {localizationStrings?.cancel}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-fill"
                        onClick={handleNext}
                        disabled={currentStep === 1 && !isStep1Valid}
                      >
                        {currentStep === 4
                          ? isEditMode
                            ? "Update"
                            : localizationStrings?.submit
                          : localizationStrings?.next}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={showSuccessModal}
        onHide={handleSuccessModalClose}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="inc-alert index-2000"
      >
        <span className="success-icon">
          <i className="icon done"></i>
        </span>
        <h3>{localizationStrings?.success}</h3>
        <p>{successMessage || localizationStrings?.reportGeneratedSuccessfully}</p>
        <Button onClick={handleSuccessModalClose}>
          {localizationStrings?.ok}
        </Button>
      </Modal>
      {errorPopup && (
        <Modal
          show={!!errorPopup}
          onHide={closeErrorPopup}
          aria-labelledby="contained-modal-title-vcenter"
          centered
          className="inc-alert index-2000"
        >
          <h2>{errorPopup.title}</h2>
          <ul>
            {errorPopup.details.map((detail, index) => (
              <li key={index}>{detail}</li>
            ))}
          </ul>
          <Button onClick={closeErrorPopup}>
            {localizationStrings?.close}
          </Button>
        </Modal>
      )}
    </div>
  );
}

export default ConfigureNetwork;
